import { formatLicenseDate } from '../utils/licenses';
import React, { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  PowerSettingsNew,
  Warning,
  Error,
  VpnKey,
  ContentCopy,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { LaserMachine } from '../types';
import { useApp } from '../contexts/AppContext';
import { machineService } from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import SurfaceCard from '../components/ui/SurfaceCard';
import FilterBar from '../components/ui/FilterBar';
import MachineFormDialog from '../components/MachineFormDialog';
import OfflineLicenseDialog from '../components/ui/OfflineLicenseDialog';
import { ConfirmationNumber } from '@mui/icons-material';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';
import MachineLicenseChips from '../components/MachineLicenseChips';

const MachinesManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, selectMachine } = useApp();
  const [machines, setMachines] = useState<LaserMachine[]>([]);
  const [filteredMachines, setFilteredMachines] = useState<LaserMachine[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMachine, setSelectedMachine] = useState<LaserMachine | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState<LaserMachine | null>(null);
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [offlineLicenseDialog, setOfflineLicenseDialog] = useState(false);
  const [newCredentials, setNewCredentials] = useState<{ apiKey: string; machineId: string; apiEndpoint: string } | null>(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const currentUser = state.auth.user;
  const isAdmin = currentUser?.role === 'administrator';
  const isReseller = currentUser?.role === 'reseller';
  const isClient = currentUser?.role === 'client';

  const loadMachines = useCallback(async () => {
    try {
      if (!currentUser) return;

      // For admins/resellers, we might want to fetch all or specific company's machines
      // Currently getMachines takes a companyId. If we pass empty string or handled by backend?
      // Let's try passing the user's companyId if they have one, or handle logic.
      // Actually, for admin, we probably want to list ALL machines.
      // Let's assume the API handles it or we need to adjust getMachines signature.
      // Looking at api.ts: getMachines: async (companyId: string)
      // We might need to pass an effective companyId or the backend ignores it for admins.

      // Admin and Reseller see all their machines; customers see only their company's
      let targetCompanyId = 'all';
      if (!isAdmin && !isReseller) {
        targetCompanyId = currentUser.companyId || '';
      }

      const machineList = await machineService.getMachines(targetCompanyId);
      setMachines(machineList);

    } catch (error) {
      console.error('Error loading machines:', error);
      setNotification({
        open: true,
        message: 'Errore nel caricamento delle macchine',
        severity: 'error'
      });
    }
  }, [currentUser, isAdmin, isReseller]);

  useEffect(() => {
    loadMachines();
  }, [loadMachines]);

  useEffect(() => {
    // Filter machines based on search and status
    let filtered = machines;

    if (searchTerm) {
      filtered = filtered.filter(machine =>
        machine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        machine.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        machine.serialNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (machine.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (machine.resellerName || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(machine => machine.status === statusFilter);
    }

    // Role-based filtering
    if (isClient && currentUser?.clientIds) {
      filtered = filtered.filter(machine => currentUser.clientIds!.includes(machine.id));
    }

    setFilteredMachines(filtered);
  }, [machines, searchTerm, statusFilter, currentUser, isClient]);

  const getStatusColor = (status: string) => {
    const s = status.toUpperCase();
    switch (s) {
      case 'ONLINE': return 'success';
      case 'OFFLINE': return 'default';
      case 'MAINTENANCE': return 'warning';
      case 'ERROR': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    const s = status.toUpperCase();
    switch (s) {
      case 'ONLINE': return <PowerSettingsNew color="success" />;
      case 'OFFLINE': return <PowerSettingsNew color="disabled" />;
      case 'MAINTENANCE': return <Warning color="warning" />;
      case 'ERROR': return <Error color="error" />;
      default: return <PowerSettingsNew />;
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, machine: LaserMachine) => {
    setAnchorEl(event.currentTarget);
    setSelectedMachine(machine);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMachine(null);
  };

  const handleAdd = () => {
    setEditingMachine(null);
    setDialogOpen(true);
  };

  const handleEdit = () => {
    if (selectedMachine) {
      setEditingMachine(selectedMachine);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialog(true);
    setAnchorEl(null);
  };

  const confirmDelete = async () => {
    if (selectedMachine) {
      try {
        await machineService.deleteMachine(selectedMachine.id);
        const updatedMachines = machines.filter(m => m.id !== selectedMachine.id);
        setMachines(updatedMachines);
        setNotification({
          open: true,
          message: 'Macchina eliminata con successo',
          severity: 'success'
        });
      } catch (error) {
        setNotification({
          open: true,
          message: 'Errore durante l\'eliminazione della macchina',
          severity: 'error'
        });
      }
    }
    setDeleteDialog(false);
    setSelectedMachine(null);
  };

  const handleSaveMachine = async (data: Partial<LaserMachine>) => {
    try {
      if (editingMachine) {
        await machineService.updateMachine(editingMachine.id, data);
        setNotification({
          open: true,
          message: 'Macchina modificata con successo',
          severity: 'success'
        });
      } else {
        await machineService.createMachine(data);
        setNotification({
          open: true,
          message: 'Macchina creata con successo',
          severity: 'success'
        });
      }
      setDialogOpen(false);
      setEditingMachine(null);
      // Reload from server to ensure consistent state
      await loadMachines();
    } catch (error: any) {
      console.error('Errore salvataggio macchina:', error?.response?.data || error);
      const msg = error?.response?.data?.error?.message || 'Errore durante il salvataggio';
      setNotification({
        open: true,
        message: msg,
        severity: 'error'
      });
    }
  };

  const handleGenerateCredentials = async (machine: LaserMachine) => {
    try {
      const response = await machineService.generateCredentials(machine.id);
      // Backend returns { success: true, data: { ... } }
      setNewCredentials(response.data);
      setCredentialsDialog(true);
    } catch (error) {
      setNotification({
        open: true,
        message: 'Errore generazione credenziali',
        severity: 'error'
      });
    }
  };

  const handleOpenOfflineLicense = (machine: LaserMachine) => {
    setSelectedMachine(machine);
    setOfflineLicenseDialog(true);
  };

  const copyToClipboard = async (text: string) => {
    const copied = await copyTextToClipboard(text);
    if (copied) {
      setNotification({
        open: true,
        message: 'Copiato negli appunti!',
        severity: 'success'
      });
      return;
    }

    promptManualCopy(text, 'il valore');
    setNotification({
      open: true,
      message: 'Clipboard non disponibile: usa copia manuale',
      severity: 'error'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const canAddMachine = isAdmin || isReseller;
  const canEditMachine = isAdmin || isReseller;
  const canDeleteMachine = isAdmin;

  const handleViewDetails = () => {
    if (selectedMachine) {
      selectMachine(selectedMachine);
      navigate(`/machines/${selectedMachine.id}`);
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <PageHeader
          title="Gestione macchine"
          subtitle="Configura inventario, assegnazione ai clienti e licenze NextControl delle macchine."
          actions={
            canAddMachine ? (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAdd}
                sx={{ minWidth: 160 }}
              >
                Aggiungi Macchina
              </Button>
            ) : null
          }
        />
      </Box>

      {/* Filters */}
      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <FilterBar>
          <TextField
            placeholder="Cerca per nome, modello, numero seriale..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Stato</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Stato"
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
              <MenuItem value="maintenance">Manutenzione</MenuItem>
              <MenuItem value="error">Errore</MenuItem>
            </Select>
          </FormControl>
        </FilterBar>
      </Box>

      {/* Machines Table */}
      {/* Machines Table */}
      <Box sx={{ mt: 4 }}>
        <SurfaceCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Modello</TableCell>
                  <TableCell>Numero Seriale</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Reseller</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Posizione</TableCell>
                  <TableCell>Scadenza Licenza</TableCell>
                  <TableCell>Licenze</TableCell>
                  <TableCell>Potenza</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMachines.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        {searchTerm || statusFilter !== 'all'
                          ? 'Nessuna macchina trovata con questi filtri'
                          : 'Nessuna macchina registrata'}
                      </Typography>
                      {canAddMachine && (
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleAdd}
                          sx={{ mt: 2 }}
                        >
                          Aggiungi Prima Macchina
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMachines.map((machine, index) => (
                    <TableRow
                      key={machine.id}
                      hover
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {machine.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{machine.model}</TableCell>
                      <TableCell>{machine.serialNumber}</TableCell>
                      <TableCell>{machine.companyName || 'Non assegnato'}</TableCell>
                      <TableCell>{machine.resellerName || 'Diretto'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(machine.status)}
                          <Chip
                            label={machine.status}
                            color={getStatusColor(machine.status) as any}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>{machine.location}</TableCell>
                      <TableCell>
                        {machine.licenseExpirationDate
                          ? formatLicenseDate(machine.licenseExpirationDate)
                          : <Chip label="A VITA" color="success" size="small" variant="outlined" />}
                      </TableCell>
                      <TableCell>
                        <MachineLicenseChips machine={machine} />
                      </TableCell>
                      <TableCell>{machine.specifications?.power || 0}W</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => handleGenerateCredentials(machine)}
                          color="primary"
                          title="Genera Credenziali HMI"
                          size="small"
                        >
                          <VpnKey />
                        </IconButton>
                        <IconButton
                          onClick={() => handleOpenOfflineLicense(machine)}
                          color="secondary"
                          title="Genera Licenza Offline"
                          size="small"
                        >
                          <ConfirmationNumber />
                        </IconButton>
                        <IconButton
                          onClick={(e) => handleMenuClick(e, machine)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SurfaceCard>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1 }} />
          Visualizza Dettagli
        </MenuItem>
        {canEditMachine && (
          <MenuItem onClick={handleEdit}>
            <Edit sx={{ mr: 1 }} />
            Modifica
          </MenuItem>
        )}
        {canDeleteMachine && (
          <MenuItem onClick={handleDelete}>
            <Delete sx={{ mr: 1 }} />
            Elimina
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare la macchina "{selectedMachine?.name}"?
            Questa azione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            Annulla
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Machine Form Dialog */}
      <MachineFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        machine={editingMachine}
        onSave={handleSaveMachine}
      />

      {/* Offline License Dialog */}
      <OfflineLicenseDialog
        open={offlineLicenseDialog}
        onClose={() => setOfflineLicenseDialog(false)}
        machine={selectedMachine}
      />

      {/* Credentials Dialog */}
      <Dialog open={credentialsDialog} onClose={() => setCredentialsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey color="primary" /> Credenziali HMI
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Salva queste informazioni ora. Per sicurezza, la chiave segreta non potrà essere recuperata in futuro.
          </Alert>

          {newCredentials && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="API Endpoint"
                value={newCredentials.apiEndpoint}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => copyToClipboard(newCredentials.apiEndpoint)}>
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Machine ID (X-Machine-ID)"
                value={newCredentials.machineId}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => copyToClipboard(newCredentials.machineId)}>
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="API Key (X-Device-Key)"
                value={newCredentials.apiKey}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => copyToClipboard(newCredentials.apiKey)}>
                        <ContentCopy />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialog(false)} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MachinesManagementPage;
